import React, { useState, useEffect } from 'react';
import AdminSubPage from '../components/AdminSubPage';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Trash2, Globe, CreditCard, Eye, EyeOff, Save, CheckCircle2, ExternalLink, CircleDot, Pencil, Plus, Activity } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

// Service type definitions with their credential fields
const SERVICE_TYPES = {
  eft: { label: 'EFT / Bank Transfer', color: 'text-blue-700 bg-blue-50', providers: {
    ozow: { label: 'Ozow', fields: ['api_key', 'private_key', 'site_code'], signupUrl: 'https://signup.ozow.com' },
    stitch: { label: 'Stitch PayOS', fields: ['client_id', 'client_secret'], signupUrl: 'https://stitch.money' },
    custom: { label: 'Other', fields: ['api_key', 'api_secret', 'merchant_id'] },
  }},
  bill_payment: { label: 'Bill Payment', color: 'text-emerald-700 bg-emerald-50', providers: {
    netcash: { label: 'Netcash', fields: ['merchant_account', 'service_key', 'software_vendor_key'], signupUrl: 'https://netcash.co.za' },
    stitch: { label: 'Stitch PayOS', fields: ['client_id', 'client_secret'], signupUrl: 'https://stitch.money' },
    custom: { label: 'Other', fields: ['api_key', 'api_secret', 'merchant_id'] },
  }},
  airtime: { label: 'Airtime / Prepaid', color: 'text-orange-700 bg-orange-50', providers: {
    reloadly: { label: 'Reloadly', fields: ['client_id', 'client_secret'], signupUrl: 'https://www.reloadly.com' },
    freepaid: { label: 'Freepaid', fields: ['user_number', 'api_key'], signupUrl: 'https://freepaid.co.za' },
    peoples_prepaid: { label: 'Peoples Prepaid', fields: ['username', 'password', 'terminal_id'], signupUrl: 'https://peoplesprepaid.co.za' },
    custom: { label: 'Other', fields: ['api_key', 'api_secret'] },
  }},
  sms: { label: 'SMS Gateway', color: 'text-indigo-700 bg-indigo-50', providers: {
    clickatell: { label: 'Clickatell', fields: ['api_key', 'sender_id'], signupUrl: 'https://www.clickatell.com' },
    africas_talking: { label: "Africa's Talking", fields: ['api_key', 'username', 'sender_id'], signupUrl: 'https://africastalking.com' },
    bulksms: { label: 'BulkSMS', fields: ['api_token_id', 'api_token_secret'], signupUrl: 'https://www.bulksms.com' },
    smsportal: { label: 'SMSPortal', fields: ['api_key', 'api_secret', 'sender_id'], signupUrl: 'https://www.smsportal.com' },
    custom: { label: 'Other', fields: ['api_key', 'api_secret', 'sender_id'] },
  }},
  credit_bureau: { label: 'Credit Bureau', color: 'text-amber-700 bg-amber-50', providers: {
    transunion: { label: 'TransUnion', fields: ['api_key', 'api_secret', 'subscriber_code', 'certificate_path'], signupUrl: 'https://www.transunion.co.za' },
    experian: { label: 'Experian', fields: ['client_id', 'client_secret', 'subscriber_id', 'bureau_endpoint'], signupUrl: 'https://www.experian.co.za' },
    xds: { label: 'XDS', fields: ['api_key', 'api_secret', 'branch_code'], signupUrl: 'https://www.xds.co.za' },
    compuscan: { label: 'Experian Sigma (CompuScan)', fields: ['client_id', 'client_secret', 'subscriber_id'], signupUrl: 'https://www.experian.co.za' },
    custom: { label: 'Other', fields: ['api_key', 'api_secret', 'subscriber_code'] },
  }},
  risk_model: { label: "Nkosi's Risk API (PD/LGD)", color: 'text-indigo-700 bg-indigo-50', providers: {
    nkosi_risk_api: { label: "Nkosi's Risk API", fields: ['api_key', 'auth_header', 'model_version', 'timeout_seconds'], signupUrl: '' },
    custom: { label: 'Custom Risk Model', fields: ['api_key', 'auth_header', 'timeout_seconds'] },
  }},
  kyc: { label: 'KYC / Identity Verification', color: 'text-teal-700 bg-teal-50', providers: {
    experian_kyc: { label: 'Experian KYC', fields: ['client_id', 'client_secret', 'subscriber_id', 'enquiry_type'], signupUrl: 'https://www.experian.co.za' },
    datanamix: { label: 'Datanamix', fields: ['api_key', 'api_secret', 'account_id'], signupUrl: 'https://www.datanamix.com' },
    verifynow: { label: 'VerifyNow (DHA)', fields: ['api_key', 'api_secret', 'institution_code'], signupUrl: 'https://www.verifynow.co.za' },
    verifyid: { label: 'VerifyID', fields: ['api_key', 'account_id'], signupUrl: 'https://www.verifyid.co.za' },
    home_affairs: { label: 'DHA Direct (Home Affairs)', fields: ['api_key', 'api_secret', 'institution_id', 'certificate_path'], signupUrl: '' },
    custom: { label: 'Other', fields: ['api_key', 'api_secret', 'account_id'] },
  }},
  aml: { label: 'AML / Transaction Monitoring', color: 'text-red-700 bg-red-50', providers: {
    facephi: { label: 'Facephi', fields: ['client_id', 'client_secret', 'tenant_id', 'webhook_url'], signupUrl: 'https://facephi.com' },
    relycomply: { label: 'RelyComply', fields: ['api_key', 'api_secret', 'organization_id'], signupUrl: 'https://relycomply.com' },
    namescan: { label: 'NameScan', fields: ['api_key', 'webhook_secret'], signupUrl: 'https://namescan.io' },
    tookitaki: { label: 'Tookitaki', fields: ['api_key', 'api_secret', 'tenant_id'], signupUrl: 'https://tookitaki.com' },
    aml_watcher: { label: 'AML Watcher', fields: ['api_key', 'api_secret'], signupUrl: 'https://amlwatcher.com' },
    vove_id: { label: 'VOVE ID', fields: ['api_key', 'api_secret', 'organization_id', 'goaml_entity_id'], signupUrl: 'https://voveid.com' },
    custom: { label: 'Other', fields: ['api_key', 'api_secret', 'organization_id'] },
  }},
  cross_border: { label: 'Cross-Border Payments', color: 'text-cyan-700 bg-cyan-50', providers: {
    tcib: { label: 'TCIB (BankservAfrica / SADC)', fields: ['participant_id', 'api_key', 'api_secret', 'settlement_bank_bic', 'certificate_path', 'scheme_id'], signupUrl: 'https://sadcbanking.org/what-we-do/introduction-to-tcib/' },
    papss: { label: 'PAPSS (Pan-African)', fields: ['participant_id', 'api_key', 'api_secret', 'institution_bic', 'settlement_currency', 'certificate_path'], signupUrl: 'https://papss.com/get-connected/' },
    custom: { label: 'Other', fields: ['api_key', 'api_secret', 'participant_id', 'institution_bic'] },
  }},
  instant_money: { label: 'Instant Money / Cash Send', color: 'text-rose-700 bg-rose-50', providers: {
    nedbank_wallet: { label: 'Nedbank MobiMoney Wallet', fields: ['client_id', 'client_secret', 'subscription_key', 'callback_url'], signupUrl: 'https://apim.nedbank.co.za' },
    ozow_instant: { label: 'Ozow Instant Pay', fields: ['api_key', 'private_key', 'site_code'], signupUrl: 'https://signup.ozow.com' },
    custom: { label: 'Other', fields: ['api_key', 'api_secret', 'merchant_id'] },
  }},
  electricity: { label: 'Prepaid Electricity', color: 'text-yellow-700 bg-yellow-50', providers: {
    kazang: { label: 'Kazang', fields: ['api_key', 'api_secret', 'terminal_id', 'vendor_id'], signupUrl: 'https://www.kazang.com/kazang-corporate-integration/' },
    freepaid_elec: { label: 'Freepaid Electricity', fields: ['user_number', 'api_key'], signupUrl: 'https://freepaid.co.za' },
    ikhokha: { label: 'iKhokha Prepaid', fields: ['api_key', 'merchant_id'], signupUrl: 'https://www.ikhokha.com/ik-prepaid' },
    custom: { label: 'Other', fields: ['api_key', 'api_secret', 'vendor_id'] },
  }},
  cardless_cash: { label: 'Cardless Cash Withdrawal', color: 'text-violet-700 bg-violet-50', providers: {
    nedbank_atm: { label: 'Nedbank Wallet ATM', fields: ['client_id', 'client_secret', 'subscription_key'], signupUrl: 'https://apim.nedbank.co.za' },
    masterpass: { label: 'Masterpass QR', fields: ['api_key', 'api_secret', 'merchant_id'], signupUrl: 'https://masterpass.com' },
    custom: { label: 'Other', fields: ['api_key', 'api_secret', 'merchant_id'] },
  }},
  payshap: { label: 'PayShap (Real-Time Payments)', color: 'text-sky-700 bg-sky-50', providers: {
    electrum: { label: 'Electrum', fields: ['api_key', 'api_secret', 'participant_id'], signupUrl: 'https://www.electrumsoftware.com/schemes/payshap' },
    ozow_payshap: { label: 'Ozow PayShap', fields: ['api_key', 'private_key', 'site_code'], signupUrl: 'https://ozow.com' },
    netcash_payshap: { label: 'Netcash RTP', fields: ['merchant_account', 'service_key', 'software_vendor_key'], signupUrl: 'https://netcash.co.za' },
    stitch_payshap: { label: 'Stitch PayShap', fields: ['client_id', 'client_secret'], signupUrl: 'https://stitch.money' },
    custom: { label: 'Other', fields: ['api_key', 'api_secret', 'participant_id'] },
  }},
  email: { label: 'Email Service', color: 'text-purple-700 bg-purple-50', providers: {
    sendgrid: { label: 'SendGrid', fields: ['api_key', 'sender_email'], signupUrl: 'https://sendgrid.com' },
    custom: { label: 'Other', fields: ['api_key', 'sender_email'] },
  }},
  other: { label: 'Other', color: 'text-gray-700 bg-gray-50', providers: { custom: { label: 'Custom', fields: ['api_key', 'api_secret'] } } },
};

const FIELD_LABELS = {
  api_key: 'API Key', private_key: 'Private Key', site_code: 'Site Code',
  api_secret: 'API Secret', merchant_id: 'Merchant ID',
  merchant_account: 'Merchant Account', service_key: 'Service Key',
  software_vendor_key: 'Software Vendor Key', client_id: 'Client ID',
  client_secret: 'Client Secret', user_number: 'User Number',
  username: 'Username', password: 'Password', terminal_id: 'Terminal ID',
  subscriber_code: 'Subscriber Code', certificate_path: 'Certificate Path',
  subscriber_id: 'Subscriber ID', bureau_endpoint: 'Bureau Endpoint',
  enquiry_type: 'Enquiry Type', account_id: 'Account ID',
  institution_code: 'Institution Code', institution_id: 'Institution ID',
  sender_id: 'Sender ID', api_token_id: 'API Token ID',
  api_token_secret: 'API Token Secret', branch_code: 'Branch Code',
  tenant_id: 'Tenant ID', webhook_url: 'Webhook URL',
  webhook_secret: 'Webhook Secret', organization_id: 'Organisation ID',
  goaml_entity_id: 'goAML Entity ID', sender_email: 'Sender Email',
  participant_id: 'Participant ID', settlement_bank_bic: 'Settlement Bank BIC',
  scheme_id: 'Scheme ID', institution_bic: 'Institution BIC',
  settlement_currency: 'Settlement Currency',
  subscription_key: 'Subscription Key', callback_url: 'Callback URL',
  vendor_id: 'Vendor ID',
};

const SECRET_FIELDS = new Set(['api_key', 'private_key', 'api_secret', 'service_key', 'software_vendor_key', 'client_secret', 'password', 'api_token_secret', 'subscription_key']);

const PAYMENT_TYPES = new Set(['eft', 'bill_payment', 'airtime', 'instant_money', 'electricity', 'cardless_cash', 'payshap']);

const emptyForm = { name: '', description: '', service_type: 'eft', provider: '', base_url: '', probe_path: '', environment: 'sandbox', is_active: true, credentials: {} };

const ExternalServicesPage = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [showSecrets, setShowSecrets] = useState({});
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [servicesRes, providersRes] = await Promise.all([
        api.get('/external-services').catch(() => ({ data: [] })),
        api.get('/admin/payment-providers').catch(() => ({ data: {} })),
      ]);

      const combined = [];

      // Add custom services
      (servicesRes.data || []).forEach(s => {
        combined.push({ ...s, source: 'custom' });
      });

      // Add payment providers
      Object.entries(providersRes.data || {}).forEach(([key, config]) => {
        if (config.is_enabled || Object.values(config.credentials || {}).some(v => v)) {
          combined.push({
            id: `provider_${key}`,
            name: config.label,
            service_type: key === 'ozow' || key === 'stitch' ? 'eft' : key === 'netcash' ? 'bill_payment' : 'airtime',
            provider: key,
            is_active: config.is_enabled || false,
            environment: config.environment || 'sandbox',
            credentials: config.credentials || {},
            source: 'provider',
          });
        }
      });

      setServices(combined);
    } catch { setServices([]); }
    setLoading(false);
  };

  const getProviderFields = () => {
    const st = SERVICE_TYPES[form.service_type];
    if (!st) return [];
    const prov = st.providers[form.provider] || st.providers['custom'] || { fields: [] };
    return prov.fields || [];
  };

  const getProviderOptions = () => {
    const st = SERVICE_TYPES[form.service_type];
    return st ? Object.entries(st.providers).map(([k, v]) => ({ value: k, label: v.label })) : [];
  };

  const getSignupUrl = () => {
    const st = SERVICE_TYPES[form.service_type];
    if (!st) return null;
    return (st.providers[form.provider] || {}).signupUrl || null;
  };

  const openAdd = () => {
    setEditId(null);
    setForm({ ...emptyForm, provider: Object.keys(SERVICE_TYPES.eft.providers)[0] });
    setShowSecrets({});
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditId(item.id);
    setForm({
      name: item.name || '',
      description: item.description || '',
      service_type: item.service_type || 'other',
      provider: item.provider || 'custom',
      base_url: item.base_url || '',
      probe_path: item.probe_path || '',
      environment: item.environment || 'sandbox',
      is_active: item.is_active !== false,
      credentials: item.credentials || {},
    });
    setShowSecrets({});
    setShowModal(true);
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      const isPayment = PAYMENT_TYPES.has(form.service_type) && form.provider !== 'custom';

      if (isPayment) {
        // Save to payment providers
        const providerKey = form.provider;
        await api.put(`/admin/payment-providers/${providerKey}`, {
          is_enabled: form.is_active,
          environment: form.environment,
          credentials: form.credentials,
        });
        toast.success(`${form.name || providerKey} saved`);
      } else {
        // Save to custom external services
        const payload = {
          name: form.name,
          description: form.description,
          service_type: form.service_type,
          provider: form.provider,
          base_url: form.base_url,
          probe_path: form.probe_path,
          environment: form.environment,
          is_active: form.is_active,
          credentials: form.credentials,
        };

        if (editId && !editId.startsWith('provider_')) {
          await api.put(`/external-services/${editId}`, payload);
        } else {
          await api.post('/external-services', payload);
        }
        toast.success('Service saved');
      }
      setShowModal(false);
      loadAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    setSaving(false);
  };

  const toggleActive = async (item) => {
    try {
      if (item.source === 'provider') {
        await api.put(`/admin/payment-providers/${item.provider}`, { is_enabled: !item.is_active, environment: item.environment, credentials: item.credentials });
      } else {
        await api.put(`/external-services/${item.id}`, { is_active: !item.is_active });
      }
      loadAll();
    } catch { toast.error('Failed to toggle'); }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete ${item.name}?`)) return;
    try {
      if (item.source === 'provider') {
        await api.put(`/admin/payment-providers/${item.provider}`, { is_enabled: false, environment: 'sandbox', credentials: {} });
      } else {
        await api.delete(`/external-services/${item.id}`);
      }
      toast.success('Deleted');
      loadAll();
    } catch { toast.error('Failed to delete'); }
  };

  const handleTest = async (item) => {
    if (!item.id || item.source === 'provider') {
      toast.info('Test Connection is only available for external services (not payment providers)');
      return;
    }
    const loading = toast.loading(`Testing ${item.name}...`);
    try {
      const { data } = await api.post(`/external-services/${item.id}/test-connection`);
      toast.dismiss(loading);
      if (data.ok) {
        toast.success(`${item.name}: HTTP ${data.status_code} • ${data.latency_ms}ms`);
      } else {
        toast.error(`${item.name} failed: ${data.error || data.status_code} (${data.latency_ms}ms)`);
      }
    } catch (e) {
      toast.dismiss(loading);
      toast.error(e.response?.data?.detail || 'Test failed');
    }
  };

  const fields = getProviderFields();
  const providerOptions = getProviderOptions();
  const signupUrl = getSignupUrl();

  return (
    <AdminSubPage title="External Services" breadcrumb={['Admin', 'System', 'External Services']} backPath="/dashboard/admin/system">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <p className="text-xs text-gray-500">Configure payment providers, SMS gateways, credit bureaus, and other external integrations. Services go live automatically once credentials are saved and enabled.</p>
          <Button onClick={openAdd} className="bg-[#1f1f1f] text-white" data-testid="add-service-btn"><Plus className="w-4 h-4 mr-2" /> Add Service</Button>
        </div>

        <div className="bg-white border border-gray-200" data-testid="external-services-table">
          <table className="w-full">
            <thead><tr className="bg-gray-50 border-b">
              <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Service</th>
              <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Type</th>
              <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Provider</th>
              <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Environment</th>
              <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Status</th>
              <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-gray-500">Actions</th>
            </tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={6} className="text-center py-8 text-gray-400">Loading...</td></tr> :
              services.length === 0 ? <tr><td colSpan={6} className="text-center py-12 text-gray-400"><Globe className="w-8 h-8 mx-auto mb-2 text-gray-300" />No services configured. Click "Add Service" to get started.</td></tr> :
              services.map(item => {
                const typeInfo = SERVICE_TYPES[item.service_type] || SERVICE_TYPES.other;
                return (
                  <tr key={item.id} className="border-b hover:bg-gray-50" data-testid={`service-row-${item.id}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="font-medium text-sm text-[#1f1f1f]">{item.name}</div>
                        {item.is_active && <CircleDot className="w-3 h-3 text-green-500" />}
                      </div>
                      {item.description && <div className="text-xs text-gray-400">{item.description}</div>}
                    </td>
                    <td className="px-4 py-3"><span className={`px-2 py-0.5 text-xs font-medium ${typeInfo.color}`}>{typeInfo.label}</span></td>
                    <td className="px-4 py-3 text-sm text-gray-600 capitalize">{item.provider === 'custom' ? '-' : (item.provider || '-').replace(/_/g, ' ')}</td>
                    <td className="px-4 py-3 text-xs text-gray-500 capitalize">{item.environment || '-'}</td>
                    <td className="px-4 py-3 text-center"><Switch checked={item.is_active !== false} onCheckedChange={() => toggleActive(item)} data-testid={`toggle-${item.id}`} /></td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleTest(item)} className="text-[#C5A059]" data-testid={`test-${item.id}`} title="Test Connection"><Activity className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => openEdit(item)} className="text-gray-500" data-testid={`edit-${item.id}`}><Pencil className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(item)} className="text-red-500" data-testid={`delete-${item.id}`}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add / Edit Service Dialog */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" aria-describedby="es-desc">
          <DialogHeader><DialogTitle>{editId ? 'Edit Service' : 'Add External Service'}</DialogTitle></DialogHeader>
          <p id="es-desc" className="sr-only">External service configuration form</p>
          <div className="space-y-4">
            {/* Service Type */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-gray-500">Service Type *</Label>
                <Select value={form.service_type} onValueChange={v => {
                  const firstProvider = Object.keys(SERVICE_TYPES[v]?.providers || {})[0] || 'custom';
                  setForm(p => ({ ...p, service_type: v, provider: firstProvider, credentials: {} }));
                }}>
                  <SelectTrigger data-testid="es-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(SERVICE_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-gray-500">Provider</Label>
                <Select value={form.provider} onValueChange={v => setForm(p => ({
                  ...p, provider: v, credentials: {},
                  name: v !== 'custom' ? (SERVICE_TYPES[p.service_type]?.providers[v]?.label || '') : p.name,
                }))}>
                  <SelectTrigger data-testid="es-provider-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {providerOptions.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Name & Description */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-gray-500">Service Name *</Label>
                <Input value={form.name} onChange={e => setForm(p => ({...p, name: e.target.value}))} placeholder="e.g., Ozow EFT" data-testid="es-name-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-gray-500">Description</Label>
                <Input value={form.description} onChange={e => setForm(p => ({...p, description: e.target.value}))} placeholder="Optional" />
              </div>
            </div>

            {/* Environment & Base URL */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-gray-500">Environment</Label>
                <Select value={form.environment} onValueChange={v => setForm(p => ({...p, environment: v}))}>
                  <SelectTrigger data-testid="es-env-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sandbox">Sandbox / Test</SelectItem>
                    <SelectItem value="live">Live / Production</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-gray-500">Base URL</Label>
                <Input value={form.base_url} onChange={e => setForm(p => ({...p, base_url: e.target.value}))} placeholder="https://api.provider.com/v1" data-testid="es-url-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-gray-500">Health Probe Path <span className="normal-case text-gray-400">(optional, for Test Connection)</span></Label>
                <Input value={form.probe_path} onChange={e => setForm(p => ({...p, probe_path: e.target.value}))} placeholder="/health or /v1/ping (leave blank to ping root)" data-testid="es-probe-path-input" />
              </div>
            </div>

            {/* Sign up link */}
            {signupUrl && (
              <div className="p-2 bg-gray-50 border border-gray-200 text-xs text-gray-600 flex items-center gap-2">
                Don't have an account? <a href={signupUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">Sign up at {form.provider} <ExternalLink className="w-3 h-3" /></a>
              </div>
            )}

            {/* Credential Fields */}
            {fields.length > 0 && (
              <div>
                <Label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">API Credentials</Label>
                <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 border border-gray-200">
                  {fields.map(field => (
                    <div key={field}>
                      <Label className="text-xs text-gray-500">{FIELD_LABELS[field] || field.replace(/_/g, ' ')}</Label>
                      <div className="relative mt-1">
                        <Input
                          type={SECRET_FIELDS.has(field) && !showSecrets[field] ? 'password' : 'text'}
                          value={form.credentials[field] || ''}
                          onChange={e => setForm(p => ({ ...p, credentials: { ...p.credentials, [field]: e.target.value } }))}
                          placeholder={`Enter ${FIELD_LABELS[field] || field}`}
                          className="pr-10 bg-white"
                          data-testid={`es-cred-${field}`}
                        />
                        {SECRET_FIELDS.has(field) && (
                          <button type="button" onClick={() => setShowSecrets(p => ({...p, [field]: !p[field]}))} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                            {showSecrets[field] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Active Toggle */}
            <div className="flex items-center gap-3">
              <Switch checked={form.is_active} onCheckedChange={v => setForm(p => ({...p, is_active: v}))} data-testid="es-active-toggle" />
              <Label className="text-sm text-gray-600">{form.is_active ? 'Enabled — service will be live' : 'Disabled — service is offline'}</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)}>Cancel</Button>
            <Button onClick={handleSubmit} disabled={saving} className="bg-[#1f1f1f] hover:bg-[#333333] text-white" data-testid="es-submit-btn">
              {saving ? <><Save className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : <><CheckCircle2 className="w-4 h-4 mr-2" /> {editId ? 'Update Service' : 'Add Service'}</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminSubPage>
  );
};
export default ExternalServicesPage;
